import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, X, Star, Trash2 } from 'lucide-react';
import api from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../lib/currencyUtils';

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl my-4">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-bold text-lg text-[#561D1A]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const TIER_COLORS = [
  '#561D1A', '#7C4DFF', '#00897B', '#F57F17', '#1565C0', '#AD1457', '#2E7D32'
];

export default function MembershipTab() {
  const { currency: activeCurrency, symbol } = useCurrency();
  const [tariffs, setTariffs] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('tariffs');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTariff, setEditTariff] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', price: 0,
    benefits: '', discount_percent: 0,
    points_per_kes: 1, color: '#561D1A'
  });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const [tRes, mRes] = await Promise.all([
      api.get('/api/membership/admin/tariffs').catch(() => ({ data: [] })),
      api.get('/api/membership/admin/memberships').catch(() => ({ data: [] })),
    ]);
    setTariffs(tRes.data);
    setMemberships(mRes.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditTariff(null);
    setForm({ name: '', description: '', price: 0, benefits: '', discount_percent: 0, points_per_kes: 1, color: '#561D1A' });
    setModalOpen(true);
  };

  const openEdit = (t) => {
    setEditTariff(t);
    setForm({ name: t.name, description: t.description || '', price: t.price, benefits: t.benefits?.join('\n') || '', discount_percent: t.discount_percent, points_per_kes: t.points_per_kes, color: t.color || '#561D1A' });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, description: form.description,
      price: parseFloat(form.price) || 0,
      benefits: form.benefits.split('\n').map(b => b.trim()).filter(Boolean),
      discount_percent: parseFloat(form.discount_percent) || 0,
      points_per_kes: parseFloat(form.points_per_kes) || 1,
      color: form.color,
    };
    try {
      if (editTariff) {
        await api.put(`/api/membership/admin/tariffs/${editTariff.id}`, payload);
        toast.success('Tier updated');
      } else {
        await api.post('/api/membership/admin/tariffs', payload);
        toast.success('Membership tier created');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Delete this tier permanently?')) return;
    await api.delete(`/api/membership/admin/tariffs/${id}`);
    toast.success('Tier deleted');
    load();
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(prev => prev.size === memberships.length ? new Set() : new Set(memberships.map(m => m.id)));
  const handleDeleteMember = async (id) => {
    if (!window.confirm('Delete this membership permanently?')) return;
    try { await api.delete(`/api/membership/admin/memberships/${id}`); toast.success('Membership deleted'); setSelected(prev => { const n = new Set(prev); n.delete(id); return n; }); load(); }
    catch { toast.error('Delete failed'); }
  };
  const handleBulkDelete = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} membership(s) permanently?`)) return;
    setDeleting(true);
    try { const res = await api.post('/api/membership/admin/memberships/bulk-delete', { ids: [...selected] }); toast.success(`${res.data.deleted} membership(s) deleted`); setSelected(new Set()); load(); }
    catch { toast.error('Bulk delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div data-testid="membership-admin-tab">
      {/* View Toggle */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex bg-white border border-gray-200 rounded-xl p-1">
          {['tariffs', 'members'].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${view === v ? 'bg-[#561D1A] text-white' : 'text-gray-500 hover:text-[#561D1A]'}`}>
              {v === 'tariffs' ? 'Tiers' : 'Members'}
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-500 flex-1">
          {view === 'tariffs' ? `${tariffs.length} tier(s)` : `${memberships.length} member(s)`}
        </p>
        {view === 'tariffs' && (
          <button onClick={openCreate} className="flex items-center gap-2 bg-[#EC1A23] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[#C4121A]" data-testid="add-tier-btn">
            <Plus size={15} /> Add Tier
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-40 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : view === 'tariffs' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tariffs.length === 0 ? (
            <div className="col-span-3 text-center py-16 text-gray-400">
              <Star size={40} className="mx-auto mb-3 text-gray-300" />
              No membership tiers yet
            </div>
          ) : tariffs.map(tier => (
            <div key={tier.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm" data-testid="tier-card">
              <div className="h-2" style={{ background: tier.color || '#561D1A' }} />
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-gray-800">{tier.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${tier.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                    {tier.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mb-3">{tier.description}</p>
                <div className="space-y-1.5 mb-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Annual Fee</span>
                    <span className="font-medium">{tier.price > 0 ? formatAmount(tier.price, activeCurrency) : 'Free'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Discount</span>
                    <span className="font-medium text-[#EC1A23]">{tier.discount_percent}%</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Points/{symbol}</span>
                    <span className="font-medium">{tier.points_per_kes}</span>
                  </div>
                </div>
                <ul className="space-y-1 mb-4">
                  {tier.benefits?.slice(0, 3).map((b, i) => (
                    <li key={i} className="text-xs text-gray-600 flex items-center gap-1.5">
                      <div className="w-1 h-1 rounded-full bg-[#D4AF37]" />{b}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(tier)} className="flex-1 border border-gray-200 text-gray-600 hover:border-[#561D1A] text-xs px-3 py-1.5 rounded-lg flex items-center justify-center gap-1" data-testid="edit-tier-btn">
                    <Edit2 size={11} /> Edit
                  </button>
                  <button onClick={() => handleDeactivate(tier.id)} className="flex-1 border border-red-100 text-red-400 hover:bg-red-50 text-xs px-3 py-1.5 rounded-lg flex items-center justify-center gap-1" data-testid="delete-tier-btn">
                    <Trash2 size={11} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Members list */
        <div>
          {selected.size > 0 && (
            <div className="flex items-center gap-3 mb-3 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="bulk-action-bar">
              <span className="text-sm text-red-700 font-medium">{selected.size} selected</span>
              <button onClick={handleBulkDelete} disabled={deleting}
                className="flex items-center gap-1.5 text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                data-testid="bulk-delete-members-btn">
                <Trash2 size={12} /> {deleting ? 'Deleting...' : 'Delete Selected'}
              </button>
              <button onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-700 ml-auto">Clear</button>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox" checked={selected.size > 0 && selected.size === memberships.length}
                      onChange={toggleAll} className="rounded border-gray-300 accent-[#EC1A23]" data-testid="select-all-members" />
                  </th>
                  {['Member', 'Tier', 'Card Number', 'Points', 'Total Spent', 'Status', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-500 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {memberships.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-10 text-gray-400">No members yet</td></tr>
                ) : memberships.map(m => (
                  <tr key={m.id} className={`hover:bg-gray-50 ${selected.has(m.id) ? 'bg-red-50/30' : ''}`} data-testid="member-row">
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleSelect(m.id)}
                        className="rounded border-gray-300 accent-[#EC1A23]" data-testid="select-member" />
                    </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{m.user_name}</p>
                    <p className="text-xs text-gray-400">{m.user_email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full text-white font-medium" style={{ background: tariffs.find(t => t.id === m.tariff_id)?.color || '#561D1A' }}>
                      {m.tariff_name}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{m.card_number}</td>
                  <td className="px-4 py-3 font-medium">{Math.floor(m.points).toLocaleString()}</td>
                  <td className="px-4 py-3 text-[#561D1A] font-bold">{formatAmount(m.total_spent, activeCurrency)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${m.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleDeleteMember(m.id)} className="text-gray-300 hover:text-red-500 p-1 rounded-lg hover:bg-red-50 transition-colors" data-testid="delete-member-btn">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <Modal title={editTariff ? 'Edit Tier' : 'Create Membership Tier'} onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Tier Name *</label>
              <input required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                placeholder="e.g. Gold, Platinum, Premium"
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="tier-name-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Description</label>
              <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                placeholder="Brief description of this tier"
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Fee ({symbol}/yr)</label>
                <input type="number" min={0} value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="tier-price-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Discount %</label>
                <input type="number" min={0} max={50} value={form.discount_percent} onChange={e => setForm(f => ({...f, discount_percent: e.target.value}))}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="tier-discount-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Pts/{symbol}</label>
                <input type="number" min={0} step={0.1} value={form.points_per_kes} onChange={e => setForm(f => ({...f, points_per_kes: e.target.value}))}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Benefits (one per line)</label>
              <textarea value={form.benefits} onChange={e => setForm(f => ({...f, benefits: e.target.value}))}
                rows={3} placeholder="Exclusive discounts&#10;Priority support&#10;Birthday bonus"
                className="w-full border-2 border-gray-200 focus:border-[#561D1A] p-2 text-sm outline-none rounded-xl bg-transparent resize-none" data-testid="tier-benefits-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Card Color</label>
              <div className="flex gap-2 flex-wrap">
                {TIER_COLORS.map(c => (
                  <button type="button" key={c} onClick={() => setForm(f => ({...f, color: c}))}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${form.color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
            <button type="submit" disabled={saving} className="w-full bg-[#EC1A23] hover:bg-[#C4121A] disabled:opacity-60 text-white py-3 rounded-full text-sm font-medium" data-testid="save-tier-btn">
              {saving ? 'Saving...' : editTariff ? 'Update Tier' : 'Create Tier'}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
